import type { EventType, WorkflowSchedule } from '../../routines.types';

import { formatNextRun } from './format-next-run';
import type { ScheduleIntl } from './schedule-intl';

/**
 * LHS / intro line for event-triggered routines (no schedule).
 * Full sentences per event type — a translated "Runs {fragment}" composition
 * cannot survive word-order and agreement differences across locales. An
 * event type missing from the select falls back to the generic sentence.
 * `ROUTINE_EVENT_TYPE_LABELS` is typed `satisfies Record<EventType, …>` so a
 * catalog gain that skips this select also fails typecheck at the label map.
 */
export function formatEventTriggerSummary(
  intl: ScheduleIntl,
  eventTypes: EventType[]
): string | null {
  const enabled = eventTypes.filter(Boolean);
  if (enabled.length === 0) return null;
  if (enabled.length === 1) {
    return intl.formatMessage(
      {
        defaultMessage:
          '{event, select, note_generated {Runs after each note} session_created {Runs when a session starts} document_created {Runs when a document is created} document_generated {Runs when a document is generated} session_transcribing {Runs while a session is transcribing} session_transcribing_stopped {Runs when transcribing stops} remote_recording_transcribed {Runs when a Heidi Remote recording is transcribed} uploaded_audio_transcribed {Runs when uploaded audio is transcribed} patient_created {Runs when a patient is created} patient_linked {Runs when a patient is linked} task_created {Runs when a task is created} session_status_changed {Runs when a session status changes} other {Runs on a trigger}}',
        id: '+VcHLgkpp+',
        description: 'Routine trigger summary for a single enabled event trigger',
      },
      { event: enabled[0]! }
    );
  }
  return intl.formatMessage(
    {
      defaultMessage:
        'Runs on {count, plural, one {# event trigger} other {# event triggers}}',
      id: 'ho69vNbCb/',
      description: 'Routine trigger summary when multiple event triggers are enabled',
    },
    { count: enabled.length }
  );
}

/**
 * A routine's trigger line, kept as parts rather than one rendered string.
 * The RHS intro needs the ``detail`` on its own to bold it, and recovering
 * that by re-parsing the rendered text breaks the moment the copy changes.
 */
type TriggerLine =
  | { kind: 'schedule'; text: string; detail: string }
  | { kind: 'event'; text: string };

/**
 * Same next-run / event line used on the focused LHS and owned-routine RHS.
 * Prefer schedule when present; otherwise summarise enabled event triggers.
 */
export function buildTriggerLine(options: {
  intl: ScheduleIntl;
  schedule?: WorkflowSchedule | null;
  eventTypes?: EventType[];
}): TriggerLine | null {
  const { intl, schedule, eventTypes = [] } = options;
  if (schedule) {
    const detail = formatNextRun(intl, schedule);
    return {
      kind: 'schedule',
      text: intl.formatMessage(
        {
          defaultMessage: 'Next run {detail}',
          id: 'HSktrnXBoZ',
          description: 'Routine trigger summary with the next scheduled run',
        },
        { detail }
      ),
      detail,
    };
  }
  const text = formatEventTriggerSummary(intl, eventTypes);
  return text ? { kind: 'event', text } : null;
}

/** Conversational sentence for the RHS intro when a real trigger is known. */
export function ownedTriggerSentence(
  intl: ScheduleIntl,
  triggerLine: TriggerLine
): string {
  return triggerLine.kind === 'schedule'
    ? intl.formatMessage(
        {
          defaultMessage: 'Next run: **{detail}**.',
          id: 'dtmnyjgDxi',
          description: 'Conversation intro showing the next scheduled routine run',
        },
        { detail: triggerLine.detail }
      )
    : intl.formatMessage(
        {
          defaultMessage: '**{text}.**',
          id: 'K7sGW29yjR',
          description:
            'Conversation intro bolding the event trigger summary, {text} is already a full sentence without terminal punctuation',
        },
        { text: triggerLine.text }
      );
}
